import {
  ButtonThemes,
  CheckCircleIcon,
  Checkbox,
  Col,
  Collapse,
  ControlLabel,
  ControlLabelPosition,
  Modal,
  ModalSizes,
  Row,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import { useCallback, useContext, useEffect, useState } from 'react';

import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import Search from 'components/__ASSESSOR__/Search/Search';
import translation from 'locales/translation';
import {
  filterStavesByName,
  getStavesById,
  saveStaves,
} from 'services/assessor';
import styles from './styles.module.scss';
interface IProps extends ModalProps {
  cooperative: string | number;
  eventId: number;
  onSend: () => void;
}

const copyStavesPrefix = translation.Assessor.Modal.CopyStaves;

export const CopyStaves = (props: IProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<boolean>(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedStave, setSelectedStave] = useState<number>();
  const [showAgendas, setShowAgendas] = useState<boolean>(false);
  const [staveOptions, setStaveOptions] = useState<
    {
      value: number;
      label: string;
    }[]
  >([]);
  const [eventOptions, setEventOptions] = useState<
    {
      value: number;
      label: string;
    }[]
  >([]);

  const { showToast } = useContext(LayoutContext);

  const handleClose = useCallback(() => {
    props.onClose && props.onClose(props.modalKey);
  }, [props]);

  const handleSubmit = useCallback(() => {
    setLoading(true);

    saveStaves({
      eventId: selectedStave,
      targetEventId: props.eventId,
      agendaIds: selectedOptions,
    })
      .then((data) => {
        setLoading(false);
        if (data.data.showWarningElectionCommissions) {
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Warning,
            showStatusBar: true,
            timeout: 10000,
            label:
              'Uma pauta Conselho foi copiada. Confira a comissão eleitoral ',
          });
        }
        props.onSend();
        handleClose();
      })
      .catch((error) => {
        setLoading(false);
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label:
            translation.Toasts.Errors.Keys[
              `${
                (error as AxiosError<Error>).response?.data.message ||
                (error as AxiosError<Error>).response?.data ||
                (error as AxiosError<Error>).message
              }` as keyof typeof translation.Toasts.Errors.Keys
            ] ?? translation.Toasts.Errors.defaultAlternative,
        });
      });
  }, [handleClose, props, selectedOptions, selectedStave, showToast]);

  useEffect(() => {
    if (!selectedStave) {
      setSelectedOptions([]);
      setShowAgendas(false);
    }
  }, [selectedStave]);

  return (
    <Modal
      title={copyStavesPrefix.Title}
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={handleClose}
      size={ModalSizes.Large}
      className={styles.root}
      footer={[
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <TimesCircleIcon />,
          children: copyStavesPrefix.buttons.cancel,
          onClick: handleClose,
        },
        {
          theme: ButtonThemes.Primary,
          leftIcon: <CheckCircleIcon />,
          children: copyStavesPrefix.buttons.copyOne,
          onClick: handleSubmit,
          disabled: loading || !selectedOptions.length,
          loading: loading,
        },
      ]}
    >
      <Row>
        <Col noPaddingTop>
          <h1 className={styles.description}>
            {copyStavesPrefix.Description()}
          </h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <Search
            label={copyStavesPrefix.Search.Label}
            placeholder={copyStavesPrefix.Search.Placeholder}
            value={searchValue!}
            onChange={(e: string) => {
              if (searchError) {
                setSearchError(false);
                setSearchErrorMessage(undefined);
              }
              setSearchValue(e);
              setShowAgendas(false);
              setSelectedOptions([]);
            }}
            onSelect={(record: { value?: number }) => {
              if (!record?.value) {
                // Clear button:
                setSearchErrorMessage(undefined);
                setSearchError(false);
                setShowAgendas(false);
                setSelectedOptions([]);
                return;
              }

              setSelectedStave(record!.value);
              getStavesById(record && record.value)
                .then(({ data }) => {
                  const hasEvent = data.length > 0;
                  setSearchError(!hasEvent);
                  setSearchErrorMessage(
                    !hasEvent
                      ? 'Não existem pautas válidas a serem copiadas'
                      : undefined
                  );
                  setShowAgendas(hasEvent);
                  setStaveOptions(
                    data.map((x: { id: number; title: string }) => {
                      return {
                        value: x.id,
                        label: x.title,
                      };
                    })
                  );
                })
                .catch((_: any) => {});
            }}
            clearButton
            data={eventOptions}
            loadData={() => {
              return new Promise((resolve: (_?: any) => void, reject) => {
                filterStavesByName({
                  eventId: props.eventId!,
                  text: searchValue!.toUpperCase(),
                })
                  .then(({ data }) => {
                    const hasEvent = data.length > 0;
                    setSearchError(!hasEvent);
                    setSearchErrorMessage(
                      !hasEvent ? 'Nenhum evento encontrado' : undefined
                    );
                    setShowAgendas(false);
                    setEventOptions(
                      data.map((e: { title: string; id: number }) => {
                        return {
                          label: e.id + ' - ' + e.title,
                          value: e.id,
                        };
                      })
                    );
                    resolve();
                  })
                  .catch((_: any) => {
                    setSearchError(true);
                    setSearchErrorMessage('Nenhum evento encontrado');
                    reject();
                  });
              });
            }}
            minLenghtToSearch={3}
            error={searchError}
            helperText={searchErrorMessage}
          />
        </Col>
      </Row>
      <Row>
        <Collapse opened={showAgendas} animateOpacity>
          <Col noPaddingTop>
            <Row className={styles.staveBox}>
              <Collapse opened={showAgendas} animateOpacity>
                {staveOptions &&
                  staveOptions.map((item, index: number) => {
                    return (
                      <div className={styles.option} key={item.value}>
                        <ControlLabel
                          label={item.label}
                          control={
                            <Checkbox
                              data-testid={`checked-list-${index}`}
                              value={selectedOptions.indexOf(item.value) > -1}
                              onChange={(val: boolean) => {
                                if (val) {
                                  setSelectedOptions([
                                    ...selectedOptions,
                                    item.value,
                                  ]);
                                } else {
                                  setSelectedOptions(
                                    selectedOptions.filter(
                                      (ids: any) => Number(ids) !== item.value
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          labelPosition={ControlLabelPosition.Right}
                        />
                      </div>
                    );
                  })}
              </Collapse>
            </Row>
          </Col>
          <Collapse opened={Boolean(staveOptions.length > 1)} animateOpacity>
            <Col>
              <ControlLabel
                label={copyStavesPrefix.SelectAll}
                control={
                  <Checkbox
                    disabled={staveOptions.length < 2 || !staveOptions.length}
                    value={selectedOptions!?.length === staveOptions!?.length}
                    onChange={(val: boolean) => {
                      if (val) {
                        setSelectedOptions(
                          staveOptions!?.map((item) => item.value)
                        );
                      } else {
                        setSelectedOptions([]);
                      }
                    }}
                  />
                }
                labelPosition={ControlLabelPosition.Right}
              />
            </Col>
          </Collapse>
        </Collapse>
      </Row>
    </Modal>
  );
};
